import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileScreen,
  faDesktop,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { HashTextRepository } from "../../Helpers/hashTextHelper"; // Adjust path if needed
import ApiServices from "../Services/ApiServices"; // Import ApiServices
import { storageHelper } from '../../Helpers/storageHelper';

const settingKey = "AllowExamOnMobile";
const DEFAULT_BLOCKER_MESSAGE =
  "لضمان نزاهة الاختبار وتوفير بيئة أداء مستقرة، يرجى استخدام جهاز كمبيوتر مكتبي أو حاسوب محمول (لابتوب).";

const MobileBlocker = ({
  children,
  forceBlock = false,
  message = DEFAULT_BLOCKER_MESSAGE,
  messageVariants,
  navigationBack = "/myexam",
}) => {
  const navigate = useNavigate();
  const [allowExam, setAllowExam] = useState(null); // null = loading, true/false = checked

  useEffect(() => {
    const checkAllowExam = async () => {
      try {
        const response = await ApiServices.get("Setting/Student/AllowExamOnMobile");

        const value = response?.data !== undefined ? response.data : response;
        const isAllowExam = value === true || String(value).toLowerCase() === "true";
        setAllowExam(isAllowExam);
      } catch (error) {
        setAllowExam(false);
      }
    };

    checkAllowExam();
  }, []);

  const containerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    initial: { y: 20, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const iconVariants = {
    initial: { scale: 0 },
    animate: {
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
      },
    },
  };
  // While loading, show nothing
  if (!forceBlock && allowExam === null) return null;

  // If allowExam is true, render the children (Exam component)
  if (allowExam && !forceBlock) return children;

  // If allowExam is false, show the blocker
  return (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate="animate"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        width: "100%",
        background:
          "linear-gradient(180deg, #f8faff 0%, #eef4ff 52%, #f6f9ff 100%)",
        color: "#1f2937",
        padding: "24px",
        fontFamily: "'Tajawal', 'Inter', sans-serif",
        textAlign: "center",
        boxSizing: "border-box",
        overflow: "hidden",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 9999,
      }}
    >
      <div style={{
        position: "absolute",
        top: "-20%",
        left: "-8%",
        width: "44vw",
        height: "44vw",
        background:
          "radial-gradient(circle, rgba(108,92,231,0.17) 0%, rgba(108,92,231,0) 72%)",
        borderRadius: "50%",
        filter: "blur(68px)",
        zIndex: 0,
        pointerEvents: "none"
      }} />
      <div style={{
        position: "absolute",
        bottom: "-20%",
        right: "-8%",
        width: "44vw",
        height: "44vw",
        background:
          "radial-gradient(circle, rgba(56,189,248,0.2) 0%, rgba(56,189,248,0) 74%)",
        borderRadius: "50%",
        filter: "blur(68px)",
        zIndex: 0,
        pointerEvents: "none"
      }} />

      <motion.div
        variants={itemVariants}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(108, 92, 231, 0.14)",
          borderRadius: "28px",
          padding: "38px 30px",
          maxWidth: "520px",
          width: "100%",
          boxShadow:
            "0 22px 52px -28px rgba(76, 70, 140, 0.5), 0 10px 28px rgba(108, 92, 231, 0.12)",
          zIndex: 1,
          position: "relative"
        }}
      >
        <motion.div
          variants={iconVariants}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "24px",
            marginBottom: "28px",
            background:
              "linear-gradient(135deg, rgba(108, 92, 231, 0.08), rgba(56, 189, 248, 0.08))",
            padding: "22px 34px",
            borderRadius: "24px",
            border: "1px solid rgba(108, 92, 231, 0.15)",
            boxShadow: "inset 0 1px 3px rgba(255, 255, 255, 0.45)"
          }}
        >
          <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <FontAwesomeIcon icon={faMobileScreen} style={{ fontSize: "40px", color: "#ef4444", opacity: 0.86 }} />
            <div style={{
              position: "absolute",
              width: "48px",
              height: "4px",
              backgroundColor: "#ef4444",
              borderRadius: "2px",
              transform: "rotate(-45deg)",
              boxShadow: "0 0 10px rgba(239,68,68,0.6)",
              zIndex: 2
            }} />
          </div>

          <div style={{ width: "2px", height: "48px", backgroundColor: "rgba(108,92,231,0.18)", borderRadius: "2px" }} />

          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <FontAwesomeIcon icon={faDesktop} style={{ fontSize: "48px", color: "#6c5ce7", filter: "drop-shadow(0 0 10px rgba(108,92,231,0.35))" }} />
          </div>
        </motion.div>

        <motion.h2 variants={itemVariants} style={{
          fontSize: "27px",
          fontWeight: "800",
          marginBottom: "14px",
          color: "#1f2a44",
          lineHeight: "1.4",
          letterSpacing: "-0.5px"
        }}>
          عذراً، لا يمكن فتح الاختبار على الهاتف المحمول
        </motion.h2>

        <motion.p variants={messageVariants || itemVariants} style={{
          fontSize: "16px",
          color: "#52607a",
          marginBottom: "30px",
          lineHeight: "1.7",
          fontWeight: "500",
          maxWidth: "90%"
        }}>
          {message}
        </motion.p>

        <motion.button
          onClick={() => navigate(navigationBack)}
          whileHover={{ scale: 1.02, boxShadow: "0 14px 30px -10px rgba(108, 92, 231, 0.48)" }}
          whileTap={{ scale: 0.97 }}
          variants={itemVariants}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            width: "100%",
            padding: "14px 22px",
            background: "linear-gradient(135deg, #6c5ce7, #4f46e5)",
            color: "#ffffff",
            border: "none",
            borderRadius: "16px",
            fontSize: "16px",
            fontWeight: "700",
            cursor: "pointer",
            transition: "all 0.3s ease",
            outline: "none",
            boxShadow: "0 8px 18px rgba(108, 92, 231, 0.35)"
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: "18px" }} />
          <span>العودة</span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default MobileBlocker;
